import classNames from "classnames";
import React, { HTMLAttributes, ReactNode } from "react";
import {
  DecorativeIcon,
  DecorativeIconColor
} from "../../Assets/DecorativeIcon/React/decorativeIcon";
import { Button } from "../../Buttons/React/button";
import { ButtonColor } from "../../Buttons/React/buttonColor";
import { ButtonSize } from "../../Buttons/React/buttonSize";

export enum AlertBoxType {
  Info = 0,
  Success = 1,
  Warning = 2,
  Important = 3,
  Error = 4
}

export interface AlertBoxLink {
  label: string;
  url: string;
  title?: string;
}

export interface AlertBoxProps {
  /** Title of the alert box */
  title?: string | ReactNode;
  /** Main text content */
  text?: string | ReactNode;
  /** Array of action links/buttons */
  links?: AlertBoxLink[];
  /** Type of alert (determines styling) */
  type?: AlertBoxType;
  /** Additional CSS classes */
  className?: string;
  /** Additional HTML attributes */
  extraAttributes?: HTMLAttributes<HTMLDivElement>;
  /** Children content (alternative to text prop) */
  children?: ReactNode;
}

/**
 * AlertBox component for displaying different types of alerts and messages.
 *
 * Supports multiple alert types with appropriate styling:
 * - Info: Informational messages (blue)
 * - Success: Success messages (green)
 * - Warning: Warning messages (yellow)
 * - Important: Important notices (pink)
 * - Error: Error messages (red)
 *
 * **Accessibility Features:**
 * - Uses role="alert" for immediate screen reader announcement
 * - Semantic heading for title
 * - Proper button labeling
 *
 * @example
 * ```tsx
 * <AlertBox
 *   type={AlertBoxType.Error}
 *   title="Something went wrong"
 *   text="An unexpected error occurred. Please try again."
 *   links={[
 *     { label: "Try again", url: "#", title: "Retry the operation" }
 *   ]}
 * />
 * ```
 */
export function AlertBox({
  title,
  text,
  links,
  type = AlertBoxType.Info,
  className = "",
  extraAttributes,
  children
}: AlertBoxProps) {
  // Determine icon based on type
  const icon =
    type === AlertBoxType.Warning ||
    type === AlertBoxType.Important ||
    type === AlertBoxType.Error
      ? "hospital"
      : "info";

  // Determine icon color based on alert type (matches AlertBox.cshtml)
  const iconColor = (() => {
    switch (type) {
      case AlertBoxType.Success:
        return DecorativeIconColor.Success;
      case AlertBoxType.Warning:
        return DecorativeIconColor.Yellow;
      case AlertBoxType.Important:
        return DecorativeIconColor.Pink600;
      case AlertBoxType.Error:
        return DecorativeIconColor.Red;
      default: // Info
        return DecorativeIconColor.Information;
    }
  })();

  // Button colors
  const getButtonColor = (isLast: boolean): ButtonColor => {
    if (type === AlertBoxType.Error) {
      return isLast ? ButtonColor.OutlineWhite : ButtonColor.Pink;
    }
    return isLast ? ButtonColor.OutlineDarkBlue : ButtonColor.DarkBlue;
  };

  // Background classes
  const backgroundClasses = classNames({
    "bg-success-100": type === AlertBoxType.Success,
    "bg-information-100": type === AlertBoxType.Info,
    "bg-warning-100": type === AlertBoxType.Warning,
    "bg-pink-400": type === AlertBoxType.Important,
    "bg-pink-1000": type === AlertBoxType.Error
  });

  // Title color classes
  const titleColorClasses = classNames({
    "text-dark-blue-600":
      type === AlertBoxType.Info ||
      type === AlertBoxType.Success ||
      type === AlertBoxType.Important ||
      type === AlertBoxType.Warning,
    "text-neutral-0": type === AlertBoxType.Error
  });

  // Text color classes
  const textColorClasses = classNames({
    "text-t-turquoise": type === AlertBoxType.Success || type === AlertBoxType.Info,
    "text-t-yellow": type === AlertBoxType.Warning,
    "text-t-pink": type === AlertBoxType.Important,
    "text-neutral-0": type === AlertBoxType.Error
  });

  return (
    <div className="@container">
      <div
        {...extraAttributes}
        role="alert"
        className={classNames(
          "w-full grid grid-cols-[min-content_auto] grid-rows-[min-content_auto]",
          "p-lg lg:p-2xl rounded-tl-br-sm md:rounded-tl-br-lg",
          backgroundClasses,
          className
        )}
      >
        {/* Icon */}
        <div className="col-span-1 col-start-1 row-span-1 @xl:row-span-full mr-md @xl:mr-lg">
          <DecorativeIcon symbol={icon} color={iconColor} />
        </div>

        {/* Title */}
        {title && (
          <div className="col-span-1 col-start-2 flex items-center @xl:items-start">
            <h3 className={classNames("text-h3", titleColorClasses)}>{title}</h3>
          </div>
        )}

        {/* Text/Children */}
        {(text || children) && (
          <div className="col-span-2 col-start-1 @xl:col-span-1 @xl:col-start-2 mt-sm @xl:mt-xs">
            <div className={classNames("text-t-sm lg:text-t-md", textColorClasses)}>
              {children || text}
            </div>
          </div>
        )}

        {/* Action Links */}
        {links && links.length > 0 && (
          <div className="col-span-2 col-start-1 @xl:col-span-1 @xl:col-start-2">
            <ul className="flex flex-col gap-md @xl:flex-row mt-lg @xl:mt-xl">
              {links.map((link, index) => {
                const isLast = index === links.length - 1 && index !== 0;
                return (
                  <li key={index}>
                    <Button
                      title={link.title || link.label}
                      color={getButtonColor(isLast)}
                      href={link.url}
                      size={ButtonSize.MD}
                      sizeLG={ButtonSize.LG}
                    >
                      {link.label}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default AlertBox;
